import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettingsPage } from '../../SettingsPageProvider';

const labelFormSchema = z.object({
  mmsi: z.boolean(),
  callSign: z.boolean(),
  imo: z.boolean(),
  typeOfShip: z.boolean(),
  typeOfCargo: z.boolean(),
  country: z.boolean(),
  destination: z.boolean(),
  size: z.boolean(),
  speedOverGround: z.boolean(),
  courseOverGround: z.boolean(),
  heading: z.boolean(),
  draught: z.boolean(),
  navigationalStatus: z.boolean(),
  aisType: z.boolean(),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

export const VesselHoverLabelForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    mmsi: settings['ribbonPlugin.settings']?.show_vessel_mmsi || false,
    aisType: settings['ribbonPlugin.settings']?.show_vessel_ais_type || false,
    callSign: settings['ribbonPlugin.settings']?.show_vessel_callsign || false,
    imo: settings['ribbonPlugin.settings']?.show_vessel_imo || false,
    typeOfShip:
      settings['ribbonPlugin.settings']?.show_vessel_shiptype || false,
    typeOfCargo:
      settings['ribbonPlugin.settings']?.show_vessel_cargotype || false,
    country: settings['ribbonPlugin.settings']?.show_vessel_country || false,
    destination:
      settings['ribbonPlugin.settings']?.show_vessel_destination || false,
    size: settings['ribbonPlugin.settings']?.show_vessel_size || false,
    speedOverGround:
      settings['ribbonPlugin.settings']?.show_vessel_sog || false,
    courseOverGround:
      settings['ribbonPlugin.settings']?.show_vessel_cog || false,
    heading: settings['ribbonPlugin.settings']?.show_vessel_heading || false,
    draught: settings['ribbonPlugin.settings']?.show_vessel_draught || false,
    navigationalStatus:
      settings['ribbonPlugin.settings']?.show_vessel_navstatus || false,
  };

  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LabelFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        show_vessel_mmsi: data.mmsi,
        show_vessel_callsign: data.callSign,
        show_vessel_imo: data.imo,
        show_vessel_shiptype: data.typeOfShip,
        show_vessel_cargotype: data.typeOfCargo,
        show_vessel_country: data.country,
        show_vessel_destination: data.destination,
        show_vessel_size: data.size,
        show_vessel_sog: data.speedOverGround,
        show_vessel_cog: data.courseOverGround,
        show_vessel_heading: data.heading,
        show_vessel_draught: data.draught,
        show_vessel_navstatus: data.navigationalStatus,
        show_vessel_ais_type: data.aisType,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-vessel-hover-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapHoverSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-vessel-hover-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapHoverFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                key="mmsi"
                control={form.control}
                name="mmsi"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverMmsiLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverMmsiDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-mmsi"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="aisType"
                control={form.control}
                name="aisType"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverAisTypeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverAisTypeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-aisType"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="callSign"
                control={form.control}
                name="callSign"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverCallsignLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverCallsignDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-callSign"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="imo"
                control={form.control}
                name="imo"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverImoLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverImoDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-imo"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="typeOfShip"
                control={form.control}
                name="typeOfShip"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverTypeOfShipLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverTypeOfShipDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-typeOfShip"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="typeOfCargo"
                control={form.control}
                name="typeOfCargo"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverTypeOfCargoLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverTypeOfCargoDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-typeOfCargo"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="country"
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverCountryLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverCountryDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-country"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="destination"
                control={form.control}
                name="destination"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverDestinationLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverDestinationDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-destination"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="size"
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverSizeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverSizeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-size"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="speedOverGround"
                control={form.control}
                name="speedOverGround"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5 ">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverSpeedOverGroundLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapShipHoverSpeedOverGroundDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-speedOverGround"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="courseOverGround"
                control={form.control}
                name="courseOverGround"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverCourseOverGroundLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapShipHoverCourseOverGroundDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-courseOverGround"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="heading"
                control={form.control}
                name="heading"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverHeadingLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverHeadingDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-heading"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="draught"
                control={form.control}
                name="draught"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverDraughtLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapShipHoverDraughtDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-draught"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="navigationalStatus"
                control={form.control}
                name="navigationalStatus"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4 space-x-2'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapShipHoverNavigationalStatusLabel}
                      </FormLabel>
                      <FormDescription>
                        {
                          messages.settingsMapShipHoverNavigationalStatusDescription
                        }
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-vesselLabel-navigationalStatus"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-vesselLabels-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
